import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ModifyOrganizationRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyOrganizationRequest';
import {Organization} from '@mintdata-wsp/common/state/DataState';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Form} from '@mintdata-wsp/wsp/ui/components/forms/data/Form';
import {Dialog} from '@mintdata-wsp/wsp/ui/components/forms/Dialog';
import {DialogButton} from '@mintdata-wsp/wsp/ui/components/forms/DialogButton';
import {ErrorPopupSide, Input, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {InputLabel} from '@mintdata-wsp/wsp/ui/components/forms/InputLabel';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./AddOrganizationDialog.less');
const cx = classnames.bind(styles);

interface IAddOrganizationDialog
{
    showValidationMessage:boolean;
    apiErrorMessage:string;
}

@observer
export class AddOrganizationDialog extends React.Component<any, IAddOrganizationDialog>
{
    private _form:Form = new Form();

    constructor(props:any)
    {
        super(props);

        this.state = {
            showValidationMessage: false,
            apiErrorMessage: null
        }
    }

    @bind
    _onCreateOrganization()
    {
        this._validateForm();

        if( this._form.areAllFieldsValid() )
        {
            const organizationName = this._form.get('organizationName');
            const organizationSiteId = this._form.get('organizationSiteId');

            const req = new ModifyOrganizationRequest(organizationName, organizationSiteId);

            controllers.user.createOrganization(req)
                .then((newOrganization:Organization) =>
                {
                    controllers.analytics.main.trackEvent(
                        AnalyticsEventActions.WSP_ORG_CREATED,
                        {
                            org_sid: newOrganization.siteId,
                            org_name: newOrganization.name
                        }
                    );

                    controllers.ui.dialogs.hideDialog(Dialogs.AddOrganization);
                    controllers.user.switchToOrganization(newOrganization);

                    this.setState({
                        showValidationMessage: true,
                        apiErrorMessage: null
                    });
                })
                .catch(error =>
                {
                    if( error instanceof ApiError )
                        this.setState({
                            showValidationMessage: true,
                            apiErrorMessage: error.message
                        });
                });
        }
        else
        {
            this.setState({
                showValidationMessage: true,
                apiErrorMessage: null
            });
        }
    }

    @bind
    private _resetValidation()
    {
        this.setState({
            showValidationMessage: false,
            apiErrorMessage: null
        });
    }

    @bind
    private _handleClose():void
    {
        controllers.ui.dialogs.hideDialog(Dialogs.AddOrganization);
    }

    private _validateForm = () =>
    {
        const fields = ['organizationName', 'organizationSiteId'];

        this._form.getFields(fields).forEach(field =>
        {
            field.elementRef.checkIsValid();
        });
    };

    @bind
    private _isOrgNameValid(value:string)
    {
        return (!this.state.apiErrorMessage || !this.state.apiErrorMessage.includes('with name'))
            && !!value && value.length >= 4 && value.length <= 128;
    }

    @bind
    private _getOrgNameErrorMessage(value:string)
    {
        if( !!this.state.apiErrorMessage && this.state.apiErrorMessage.includes('with name') )
            return this.state.apiErrorMessage
        else
            return `Please enter a valid name for your organization | From 4 to 128 symbols`;
    }

    @bind
    private _isSiteIdValid(value:string)
    {
        return (!this.state.apiErrorMessage || !this.state.apiErrorMessage.includes('with site'))
            && !!value && value.length >= 4 && value.length <= 64 && /^[a-z0-9]+$/.test(value);
    }

    @bind
    private _getSiteIdErrorMessage(value:string)
    {
        if( !!this.state.apiErrorMessage && this.state.apiErrorMessage.includes('with site') )
            return this.state.apiErrorMessage
        else
            return `Please enter a valid site id for your organization | From 4 to 64 symbols; Only "a-z 0-9" symbols are valid`;
    }

    render()
    {
        const rootDomain = WindowLocationUtil.getRootDomain();

        return (
            <Dialog
                title={'New organization'}
                onClose={this._handleClose}
            >
                <Column className={cx('contentColumn')}>

                    <InputLabel
                        label={'Pick a name for your new organization'}
                        className={cx('siteRowLabel')}
                    />
                    <Input
                        className={cx('inputField')}
                        icon={MDIcons.common.Checkmark}
                        style={InputStyle.Dialog}
                        placeholder={'Acme, Inc 42'}
                        isInitiallyFocused={true}
                        isValidFn={this._isOrgNameValid}
                        getErrorMessageFn={this._getOrgNameErrorMessage}
                        formKey={'organizationName'}
                        form={this._form}
                        onEnterKey={this._onCreateOrganization}
                        showValidationMessage={this.state.showValidationMessage}
                        onChange={this._resetValidation}
                    />

                    <Row className={cx('siteRow')}>
                        <Column className={cx('grow')}>
                            <InputLabel
                                label={'Pick a site URL for your organization'}
                                className={cx('siteRowLabel')}
                            />
                            <Row>
                                <Input
                                    className={cx('siteId', 'grow')}
                                    icon={MDIcons.common.World}
                                    style={InputStyle.Dialog}
                                    placeholder={'acme'}
                                    errorPopupSide={ErrorPopupSide.Bottom}
                                    isValidFn={this._isSiteIdValid}
                                    getErrorMessageFn={this._getSiteIdErrorMessage}
                                    formKey={'organizationSiteId'}
                                    form={this._form}
                                    onEnterKey={this._onCreateOrganization}
                                    showValidationMessage={this.state.showValidationMessage}
                                    onChange={this._resetValidation}
                                />
                                <div className={cx('siteFixedHostPart')}>
                                    .{rootDomain}
                                </div>
                            </Row>
                        </Column>
                    </Row>

                    <DialogButton
                        caption={'Create organization'}
                        onClick={this._onCreateOrganization}
                    />

                </Column>
            </Dialog>
        );
    }
}
